import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  ChevronRight,
  CheckCircle2,
  PlayCircle,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Zap,
  Waypoints
} from 'lucide-react';

interface WorkflowGuideProps {
  setActiveTab: (tab: string) => void;
}

const workflowSteps = [
  {
    title: '1. Describe & Review',
    tab: 'review',
    summary: 'Analyze what the agent does and detect threat vectors.',
    detail: 'Describe the agent’s purpose, tools, and authority. AgenticScale automatically discovers prompt injection, vendor mutation, and blast radius risks.',
    icon: Search,
    color: 'text-brand-600 dark:text-brand-400',
  },
  {
    title: '2. Operating Boundaries',
    tab: 'profiles',
    summary: 'Configure allowed tools, limits, and human approvals.',
    detail: 'Establish the safety contract stored permanently in Cloudflare D1: permitted APIs, blocked functions, autonomous spending caps, and dual-control rules.',
    icon: SlidersHorizontal,
    color: 'text-indigo-600 dark:text-indigo-400',
  },
  {
    title: '3. Pre-Release Test',
    tab: 'validate',
    summary: 'Validate against 12 behavioral and OWASP test suites.',
    detail: 'Execute automated pre-flight red-teaming checks and export a validation report mapped to NIST AI RMF controls before promoting the agent version.',
    icon: CheckCircle2,
    color: 'text-amber-600 dark:text-amber-400',
  },
  {
    title: '4. Runtime Protection',
    tab: 'simulate',
    summary: 'Intercept tool actions at the edge (ALLOW / REVIEW / BLOCK).',
    detail: 'The edge gateway evaluates in-flight tool parameters with deterministic rules and Cloudflare Workers AI semantic guardrails before execution.',
    icon: ShieldCheck,
    color: 'text-emerald-600 dark:text-emerald-400',
  },
];

export const WorkflowGuide: React.FC<WorkflowGuideProps> = ({ setActiveTab }) => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveStep((current) => (current + 1) % workflowSteps.length);
    }, 3500);
    return () => window.clearInterval(timer);
  }, []);

  const selectedStep = workflowSteps[activeStep];

  return (
    <section className="glass-panel rounded-2xl p-6 sm:p-8" aria-labelledby="workflow-guide-title">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-brand-600 dark:text-brand-400">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Interactive Safety Lifecycle</span>
          </div>
          <h2 id="workflow-guide-title" className="text-lg font-bold text-slate-900 dark:text-white mt-1">
            How Continuous Safety Assurance Works
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            From initial agent concept to deterministic edge runtime defense.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setActiveTab('review')}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 text-xs font-bold shadow-sm transition"
        >
          <Zap className="w-3.5 h-3.5" />
          <span>Start 60-Second Guided Tour</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
        
        {/* Step Carousel / Grid */}
        <div className="min-w-0 lg:col-span-8">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
            {workflowSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === activeStep;
              return (
                <button
                  key={step.title}
                  type="button"
                  onClick={() => setActiveStep(index)}
                  aria-pressed={isActive}
                  className={`relative flex min-w-0 flex-col rounded-xl border p-4 text-left transition-all sm:min-h-[140px] ${
                    isActive
                      ? 'border-brand-300 bg-brand-50/80 dark:border-brand-500/50 dark:bg-brand-950/40 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-brand-200 hover:bg-brand-50/40 dark:border-slate-800 dark:bg-slate-900/70 dark:hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold shadow-sm dark:bg-slate-950 ${step.color}`}>
                      {index + 1}
                    </span>
                    <Icon className={`h-4 w-4 shrink-0 ${step.color}`} />
                  </div>
                  <div className="mt-2.5 font-bold text-xs text-slate-900 dark:text-white leading-tight">{step.title}</div>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">{step.summary}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-4 rounded-xl border border-brand-100 bg-brand-50/60 p-4 text-xs text-brand-900 dark:border-brand-900/40 dark:bg-brand-950/20 dark:text-brand-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <span className="font-bold">{selectedStep.title}:</span> {selectedStep.detail}
            </div>
            <button
              type="button"
              onClick={() => setActiveTab(selectedStep.tab)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 text-white font-bold text-xs hover:bg-brand-500 transition shadow-sm"
            >
              <span>Explore This Step</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Quick-Start Journey Panel */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-5 dark:border-slate-800 dark:bg-slate-900/70 lg:col-span-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-900 dark:text-white">Quick-Start Interactive Tour</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300 font-semibold">5 Stages</span>
          </div>
          <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
            Follow this path to see AgenticScale protect an AI financial agent from conception to runtime defense:
          </p>
          
          <div className="space-y-1.5 pt-1">
            <button 
              type="button" 
              onClick={() => setActiveTab('review')} 
              className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300 dark:hover:text-brand-300 shadow-sm"
            >
              <span className="font-mono text-brand-600 font-bold">1</span>
              <span className="flex-1">Risk Review & Threats</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <button 
              type="button" 
              onClick={() => setActiveTab('profiles')} 
              className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300 dark:hover:text-brand-300 shadow-sm"
            >
              <span className="font-mono text-indigo-600 font-bold">2</span>
              <span className="flex-1">Operating Boundaries</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <button 
              type="button" 
              onClick={() => setActiveTab('validate')} 
              className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300 dark:hover:text-brand-300 shadow-sm"
            >
              <span className="font-mono text-amber-600 font-bold">3</span>
              <span className="flex-1">Pre-Release Validation</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <button 
              type="button" 
              onClick={() => setActiveTab('simulate')} 
              className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300 dark:hover:text-brand-300 shadow-sm"
            >
              <span className="font-mono text-emerald-600 font-bold">4</span>
              <span className="flex-1">Runtime Gateway Simulator</span>
              <PlayCircle className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <button 
              type="button" 
              onClick={() => setActiveTab('observability')} 
              className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300 dark:hover:text-brand-300 shadow-sm"
            >
              <span className="font-mono text-rose-600 font-bold">5</span>
              <span className="flex-1">Multi-Agent Delegation DAG</span>
              <Waypoints className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};
